import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { getSurveyTranslations } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES } from "@/common/constants/languages";

import StandardNotices from "./StandardNotices";

const SOFT_CHAR_LIMIT = 240;
const HARD_CHAR_LIMIT = 1600;

interface ChatMessageBoxProps {
  supportedLanguages: string[];
  messageText: string;
  setMessageText: (text: string) => void;
  onSendMessage: (messages: Array<{ content: string; lang: string }>) => void;
  sending: boolean;
  autoReplyEnabled: boolean;
  autoReplyMessage?: string;
  oneWayNotificationMessage?: string;
  selectedTemplateContent?: Array<{
    lang: string;
    content: string;
    is_verified?: boolean;
    is_ai_translated?: boolean;
  }>;
  onClearTemplate?: () => void;
  isManagedOnlineReporting?: boolean;
  isManagedRegistry?: boolean;
  canSendFreeformMessage?: boolean;
  // Blocks typing/translating/sending while still allowing a template to be
  // pre-loaded from the sidebar (used before a phone number is entered).
  disabled?: boolean;
}

const ChatMessageBox = ({
  supportedLanguages,
  messageText,
  setMessageText,
  onSendMessage,
  sending,
  autoReplyEnabled,
  autoReplyMessage,
  oneWayNotificationMessage,
  selectedTemplateContent,
  onClearTemplate,
  isManagedOnlineReporting = false,
  isManagedRegistry = false,
  canSendFreeformMessage = true,
  disabled = false,
}: ChatMessageBoxProps) => {
  const { t } = useTranslation("message");
  const [showTranslation, setShowTranslation] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [onDemandTranslation, setOnDemandTranslation] = useState<string | null>(
    null,
  );
  const [hasShownLimitWarning, setHasShownLimitWarning] = useState(false);
  const isMenuOpen = Boolean(anchorEl);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMessageTextChange = (text: string) => {
    // Enforce hard character limit
    if (text.length > HARD_CHAR_LIMIT) {
      // Show warning only once
      if (!hasShownLimitWarning) {
        toast.warning(
          t("messageBox.warnings.characterLimit", { count: HARD_CHAR_LIMIT }),
        );
        setHasShownLimitWarning(true);
      }
      return;
    }

    // Reset warning flag when text is back within limit
    if (hasShownLimitWarning && text.length <= HARD_CHAR_LIMIT) {
      setHasShownLimitWarning(false);
    }

    setMessageText(text);

    if (
      selectedTemplateContent &&
      selectedTemplateContent.length > 0 &&
      onClearTemplate
    ) {
      onClearTemplate();
    }
  };

  const handleLanguageSelect = async (languageCode: string) => {
    setSelectedLanguage(languageCode);
    setShowTranslation(true);
    handleLanguageMenuClose();

    // Check if we have template content for this language
    const templateContent = selectedTemplateContent?.find(
      (c) => c.lang === languageCode,
    );

    if (templateContent) {
      // Use existing template translation
      setOnDemandTranslation(null);
    } else if (messageText.trim()) {
      // Perform on-demand translation
      setIsTranslating(true);
      try {
        const response = await getSurveyTranslations({
          body: {
            source_strings: [{ text: messageText }],
            target_language: languageCode,
          },
        });

        if (
          response.data?.translations &&
          response.data.translations.length > 0
        ) {
          const translatedText =
            response.data.translations[0].translations[0].text;
          setOnDemandTranslation(translatedText);
        }
      } catch (error) {
        console.error("Translation failed:", error);
        toast.error(t("messageBox.errors.translationFailed"));
        setShowTranslation(false);
        setSelectedLanguage(null);
      } finally {
        setIsTranslating(false);
      }
    }
  };

  // Get translated content from template if available, or return on-demand translation
  const getTranslatedContent = () => {
    if (!selectedLanguage) {
      return null;
    }

    // First check if we have template content for this language
    if (selectedTemplateContent && selectedTemplateContent.length > 0) {
      const templateContent = selectedTemplateContent.find(
        (c) => c.lang === selectedLanguage,
      );
      if (templateContent) {
        return templateContent;
      }
    }

    // If no template content, return on-demand translation if available
    if (onDemandTranslation) {
      return {
        content: onDemandTranslation,
        lang: selectedLanguage,
        is_verified: false,
        is_ai_translated: true,
      };
    }

    return null;
  };

  const handleSendClick = () => {
    const messages: Array<{ content: string; lang: string }> = [
      { content: messageText, lang: "en" },
    ];

    // If translation is shown and available, add the translated message
    if (showTranslation && selectedLanguage) {
      const translatedContent = getTranslatedContent();
      if (translatedContent) {
        messages.push({
          content: translatedContent.content,
          lang: selectedLanguage,
        });
      }
    }

    onSendMessage(messages);

    // Clear translation state after sending
    setShowTranslation(false);
    setSelectedLanguage(null);
    setOnDemandTranslation(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendClick();
    }
  };

  // Check if template has any translations (excluding English)
  const hasTemplateTranslations =
    selectedTemplateContent &&
    selectedTemplateContent.length > 0 &&
    selectedTemplateContent.some((content) => content.lang !== "en");

  const isTranslateDisabled =
    disabled ||
    !messageText.trim() ||
    (selectedTemplateContent &&
      selectedTemplateContent.length > 0 &&
      !hasTemplateTranslations);

  return (
    <div>
      <div
        className="m-3 p-2"
        style={{ border: "1px solid var(--divider)", borderRadius: "0.25rem" }}
      >
        {autoReplyEnabled &&
          (autoReplyMessage || oneWayNotificationMessage) && (
            <StandardNotices
              autoReplyMessage={autoReplyMessage}
              oneWayNotificationMessage={oneWayNotificationMessage}
            />
          )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <Box sx={{ position: "relative", flex: 1 }}>
            <Tooltip
              title={
                disabled
                  ? t("messageBox.enterPhoneToEnable")
                  : !canSendFreeformMessage
                    ? t("messageBox.templateOnlyTooltip")
                    : ""
              }
              placement="top"
            >
              <span>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  value={messageText}
                  onChange={(e) => handleMessageTextChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t("messageBox.placeholder")}
                  variant="outlined"
                  size="small"
                  disabled={
                    disabled ||
                    isManagedOnlineReporting ||
                    isManagedRegistry ||
                    !canSendFreeformMessage
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                position: "absolute",
                bottom: 4,
                left: 8,
                fontSize: "0.7rem",
              }}
            ></Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            px: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {messageText.length <= SOFT_CHAR_LIMIT
              ? t("messageBox.characters", {
                  count: messageText.length,
                  limit: SOFT_CHAR_LIMIT,
                })
              : t("messageBox.charactersWithMax", {
                  count: messageText.length,
                  limit: SOFT_CHAR_LIMIT,
                  max: HARD_CHAR_LIMIT,
                })}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {selectedTemplateContent && selectedTemplateContent.length > 0 && (
              <IconButton
                size="small"
                color="error"
                onClick={onClearTemplate}
                aria-label={t("messageBox.clearTemplate")}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
            {isTranslateDisabled ? (
              <Tooltip
                title={
                  disabled
                    ? t("messageBox.enterPhoneToEnable")
                    : !messageText.trim()
                      ? t("messageBox.typeToTranslate")
                      : t("messageBox.noTemplateTranslation")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleLanguageMenuOpen}
                    aria-label={t("messageBox.translate")}
                    aria-controls={isMenuOpen ? "language-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen ? "true" : undefined}
                    disabled={isTranslateDisabled}
                  >
                    <TranslateIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <IconButton
                size="small"
                color="primary"
                onClick={handleLanguageMenuOpen}
                aria-label={t("messageBox.translate")}
                aria-controls={isMenuOpen ? "language-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? "true" : undefined}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            )}
            <Button
              variant="contained"
              size="small"
              endIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendClick}
              disabled={
                disabled ||
                sending ||
                (!isManagedOnlineReporting &&
                  !isManagedRegistry &&
                  !messageText.trim()) ||
                (!canSendFreeformMessage &&
                  !(
                    selectedTemplateContent &&
                    selectedTemplateContent.length > 0
                  )) ||
                messageText.length > HARD_CHAR_LIMIT
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 2,
              }}
            >
              {sending ? t("messageBox.sending") : t("messageBox.send")}
            </Button>
          </Box>

          <Menu
            id="language-menu"
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleLanguageMenuClose}
            MenuListProps={{
              "aria-labelledby": "translate-button",
            }}
          >
            {(() => {
              // For templates: show only languages available in the template
              // For user input: show all supported languages (from all templates)
              const languagesToShow =
                selectedTemplateContent && selectedTemplateContent.length > 0
                  ? selectedTemplateContent
                      .map((content) => content.lang)
                      .filter((lang) => lang !== "en") // Exclude English as it's the default
                  : supportedLanguages.filter((lang) => lang !== "en"); // Exclude English for user input too

              // Sort languages by their display name
              const sortedLanguages = languagesToShow.sort((a, b) => {
                const langA = SUPPORTED_LANGUAGES.find((l) => l.code === a);
                const langB = SUPPORTED_LANGUAGES.find((l) => l.code === b);
                if (!langA || !langB) return 0;
                return langA.name.localeCompare(langB.name);
              });

              return sortedLanguages.map((lang) => {
                const lang_info = SUPPORTED_LANGUAGES.find(
                  (l) => l.code === lang,
                );

                if (!lang_info) {
                  return null;
                }

                const langContent = selectedTemplateContent?.find(
                  (c) => c.lang === lang_info.code,
                );
                const hasTemplateTranslation = !!langContent;
                const isVerified = langContent?.is_verified;
                const canTranslateOnDemand = messageText.trim().length > 0;

                const menuItem = (
                  <MenuItem
                    key={lang_info.code}
                    onClick={() => handleLanguageSelect(lang_info.code)}
                    selected={lang_info.code === selectedLanguage}
                    disabled={
                      !hasTemplateTranslation &&
                      !canTranslateOnDemand &&
                      selectedTemplateContent &&
                      selectedTemplateContent.length > 0
                    }
                  >
                    {lang_info.code === selectedLanguage && (
                      <ListItemIcon>
                        <CheckIcon fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText inset={lang_info.code !== selectedLanguage}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          paddingRight: "2rem",
                        }}
                      >
                        <span>{lang_info.name}</span>
                        {hasTemplateTranslation && !isVerified && (
                          <Tooltip title={t("messageBox.aiGeneratedTooltip")}>
                            <Chip
                              label={t("messageBox.aiTranslated")}
                              size="small"
                              color="info"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </ListItemText>
                  </MenuItem>
                );

                // Wrap with tooltip if the language has help text
                if ("tooltip" in lang_info && lang_info.tooltip) {
                  return (
                    <Tooltip
                      key={lang_info.code}
                      title={lang_info.tooltip as string}
                      placement="right"
                      arrow
                    >
                      <span>{menuItem}</span>
                    </Tooltip>
                  );
                }

                return menuItem;
              });
            })()}
          </Menu>
        </Box>
        {showTranslation &&
          (() => {
            const translatedContent = getTranslatedContent();
            const languageName =
              SUPPORTED_LANGUAGES.find((l) => l.code === selectedLanguage)
                ?.name || t("messageBox.translationFallback");

            return (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  position: "relative",
                  borderTop: "1px solid var(--divider)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    pr: 3,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    <strong>{languageName}:</strong>
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowTranslation(false);
                    setSelectedLanguage(null);
                    setOnDemandTranslation(null);
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  }}
                  aria-label={t("messageBox.closeTranslation")}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography variant="body2">
                  {isTranslating ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <span>{t("messageBox.translating")}</span>
                    </Box>
                  ) : translatedContent ? (
                    translatedContent.content
                  ) : (
                    t("messageBox.translationUnavailable", {
                      language: languageName,
                    })
                  )}
                </Typography>
                {translatedContent && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      display: "block",
                      mt: 1,
                    }}
                  >
                    {translatedContent.content.length <= SOFT_CHAR_LIMIT
                      ? t("messageBox.characters", {
                          count: translatedContent.content.length,
                          limit: SOFT_CHAR_LIMIT,
                        })
                      : t("messageBox.charactersWithMax", {
                          count: translatedContent.content.length,
                          limit: SOFT_CHAR_LIMIT,
                          max: HARD_CHAR_LIMIT,
                        })}
                  </Typography>
                )}
              </Box>
            );
          })()}
      </div>
      {/* <Box sx={{ mt: 2, ml: 2, display: "flex", gap: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {messageText.length} characters
        </Typography>
      </Box> */}
    </div>
  );
};

export default ChatMessageBox;
